import React, { FC, useContext, useState } from 'react'
import cn from 'classnames'
import styles from './Sidebar.module.css'
import { Theme } from '../../hocs'
import { Icon, IconType } from '../Icon'
import { LanguageButton } from '../LanguageButton'
import { useTrans } from '../../hooks/useTrans'
import { useOnClickOutside } from '../../hooks/useClickOutside'

type Button = {
  name: string
  icon: IconType
  selected: boolean
  onClick: () => void
  className?: string
}

type Props = {
  buttons: (Button | false)[]
  mobile?: JSX.Element | JSX.Element[]
  onClose: () => void
  onDisconnectWallet?: () => void
}

export const Sidebar: FC<Props> = ({ buttons, onClose, mobile }) => {
  const { toggleTheme } = useContext(Theme)
  const { translate } = useTrans()
  const darkMode = translate('dark_mode', 'Dark Mode')

  const [isOpen, setIsOpen] = useState(false)

  const close = () => setIsOpen(false)
  const clickRef = React.useRef<HTMLDivElement>(null)
  useOnClickOutside(clickRef, close)

  return (
    <div>
      <div
        className={cn(styles.root, { [styles.open]: isOpen })}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={close}
        ref={clickRef}
      >
        <div className={styles.content}>
          <a href='https://orionprotocol.io' target='_blank' rel='noreferrer'>
            <Icon icon='logo' className={styles.logo} />
          </a>

          {buttons.filter(Boolean).map((button: Button) => {
            const { icon, name, selected, className, onClick } = button
            return (
              <div
                key={name}
                onClick={() => {
                  close()
                  onClick()
                }}
                className={cn(
                  styles.button,
                  {
                    [styles.selected]: selected
                  },
                  className
                )}
              >
                <div className={styles.buttonIconWrapper}>
                  <Icon icon={icon} className={styles.buttonIcon} />
                </div>
                <div className={styles.name}>{translate(name)}</div>
              </div>
            )
          })}

          <div
            className={cn(styles.button, styles.toggleThemeButton)}
            onClick={() => {
              toggleTheme()
              close()
            }}
          >
            <Icon icon='theme' className={styles.themeSwitch} />
            <div className={styles.name}>{darkMode}</div>
          </div>

          <LanguageButton onChangeLanguage={close} />
        </div>
      </div>

      <div className={styles.mobileSidebar}>
        <div
          onClick={() => {
            onClose()
            setIsOpen(!isOpen)
          }}
        >
          <Icon icon='logo' className={styles.mobileLogo} />
        </div>
        <div
          onClick={() => {
            onClose()
            setIsOpen(!isOpen)
          }}
        >
          <Icon icon='menu' className={styles.menu} />
        </div>

        {mobile}
      </div>
    </div>
  )
}
